import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { renderAdjustReason } from "../lib/adjustReason";
import { TargetExplainerSheet } from "./TargetExplainerSheet";
import { todayLocal } from "../lib/date";
import type { GoalAdjustment } from "@shared/types";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

type Props = {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
};

export function AdjustmentHistorySheet({ open, onClose, onRefresh }: Props) {
  const t = useT();
  const [rows, setRows] = useState<GoalAdjustment[]>([]);
  const [loading, setLoading] = useState(false);
  const [why, setWhy] = useState<GoalAdjustment | null>(null);
  const [calibrating, setCalibrating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.listGoalAdjustments().then(setRows).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  async function recalibrateNow() {
    setCalibrating(true);
    setMsg(null);
    try {
      await api.recalibrate(todayLocal(), false);
      const updated = await api.listGoalAdjustments();
      setRows(updated);
      onRefresh?.();
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setCalibrating(false);
    }
  }

  return (
    <SheetShell onClose={onClose}>
      <div className="space-y-4 pt-2">
        <h2 className="text-lg font-semibold">{t("goals.see_adjustment_history")}</h2>

        <button
          onClick={recalibrateNow}
          disabled={calibrating}
          className="w-full py-3 bg-accent-move/20 text-accent-move rounded-2xl text-sm font-medium disabled:opacity-50"
        >
          {calibrating ? t("common.loading") : t("goals.recalibrate_now")}
        </button>

        {msg && <p className="text-danger text-sm">{msg}</p>}

        {loading ? (
          <p className="text-fg-muted text-sm">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="text-fg-muted text-sm">{t("goals.no_adjustments")}</p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <Card key={r.id} className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-fg-secondary">
                    {new Date(r.applied_at * 1000).toLocaleDateString()}
                  </span>
                  <span className="text-fg-muted text-sm">{r.trigger}</span>
                </div>
                <div className="text-base tabular-nums">
                  {r.daily_calories_before} → {r.daily_calories_after} kcal
                </div>
                {r.reason && (
                  <div className="text-sm text-fg-muted">{renderAdjustReason(r.reason, t)}</div>
                )}
                <button onClick={() => setWhy(r)} className="text-sm text-accent-move underline press">
                  {t("adjust.see_why")}
                </button>
              </Card>
            ))}
          </div>
        )}

        <Button variant="secondary" fullWidth onClick={onClose}>
          {t("common.close")}
        </Button>
        {why && <TargetExplainerSheet adj={why} onClose={() => setWhy(null)} />}
      </div>
    </SheetShell>
  );
}
